const MODULES = {
  media: '@theme/media',
  slideshow: '@theme/slideshow',
};

/** @type {Map<string, Promise<unknown>>} */
const modulePromises = new Map();

function loadModule(key) {
  const specifier = MODULES[key];
  if (!specifier) return Promise.resolve();

  let promise = modulePromises.get(key);
  if (!promise) {
    promise = import(specifier).catch((error) => {
      modulePromises.delete(key);
      throw error;
    });
    modulePromises.set(key, promise);
  }

  return promise;
}

/** @param {HTMLElement} section */
async function loadSectionRuntime(section) {
  if (section.dataset.longFormRuntimeLoaded === 'true' || section.dataset.longFormRuntimeLoading === 'true') return;

  const modules = (section.dataset.longFormRuntime || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  if (modules.length === 0) return;

  section.dataset.longFormRuntimeLoading = 'true';

  try {
    await Promise.all(modules.map(loadModule));
    section.dataset.longFormRuntimeLoaded = 'true';
  } catch (error) {
    console.warn('[product-long-form] Deferred runtime unavailable; will retry on next intent.', error);
  } finally {
    delete section.dataset.longFormRuntimeLoading;
  }
}

/** @param {HTMLElement} section */
function bindIntentFallback(section) {
  const load = () => loadSectionRuntime(section);
  section.addEventListener('pointerenter', load, { once: true });
  section.addEventListener('focusin', load, { once: true });
  section.addEventListener('touchstart', load, { once: true, passive: true });

  section.addEventListener(
    'click',
    async (event) => {
      if (section.dataset.longFormRuntimeLoaded === 'true') return;

      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!(button instanceof HTMLButtonElement) || !section.contains(button)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      await loadSectionRuntime(section);

      if (section.dataset.longFormRuntimeLoaded === 'true' && button.isConnected) {
        button.click();
      }
    },
    { capture: true }
  );
}

function observeSections() {
  const sections = /** @type {HTMLElement[]} */ (
    Array.from(document.querySelectorAll('.product-long-form[data-long-form-runtime]:not([data-long-form-runtime=""])'))
  );

  if (sections.length === 0) return;

  if (!('IntersectionObserver' in window)) {
    sections.forEach((section) => loadSectionRuntime(section));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const section = /** @type {HTMLElement} */ (entry.target);
        observer.unobserve(section);
        loadSectionRuntime(section);
      }
    },
    { rootMargin: '900px 0px' }
  );

  for (const section of sections) {
    bindIntentFallback(section);
    observer.observe(section);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeSections, { once: true });
} else {
  observeSections();
}
